import {
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'className' | 'onChange' | 'size'
> {
  label?: ReactNode;
  indeterminate?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  ref?: Ref<HTMLInputElement>;
  /** Preferred: receives (checked, indeterminate) — never fires with both true. */
  onCheckedChange?: (checked: boolean) => void;
  /** Kept for form library interop that passes the raw event. */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

/** Checkbox with tri-state support.
 *  The native input drives keyboard / form submission / Tab order; visual
 *  chrome is entirely our own (`.vq-checkbox__box` + SVG check glyph).
 *  `indeterminate` must be set on the element via ref (not reachable through JSX). */
export function Checkbox(props: CheckboxProps): ReactNode {
  const {
    label,
    indeterminate = false,
    invalid = false,
    size = 'md',
    className,
    ref,
    onCheckedChange,
    onChange,
    checked,
    disabled,
    ...rest
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref !== undefined && ref !== null) {
        (ref as { current: HTMLInputElement | null }).current = node;
      }
    },
    [ref],
  );

  // `indeterminate` is only reachable via JS property — not a React attr.
  useEffect(() => {
    const el = inputRef.current;
    if (el !== null) el.indeterminate = indeterminate;
  }, [indeterminate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(e);
    onCheckedChange?.(e.target.checked);
  };

  const stateAttr =
    disabled === true
      ? 'disabled'
      : invalid
        ? 'invalid'
        : indeterminate
          ? 'indeterminate'
          : checked === true
            ? 'checked'
            : 'unchecked';

  const classes = ['vq-checkbox', className].filter(Boolean).join(' ');

  return (
    <label className={classes} data-component="Checkbox" data-size={size} data-state={stateAttr}>
      <input
        {...rest}
        ref={setRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-checked={indeterminate ? 'mixed' : checked === true}
        onChange={handleChange}
      />
      <span className="vq-checkbox__box" aria-hidden="true">
        {indeterminate ? (
          <svg viewBox="0 0 16 16" className="vq-checkbox__dash">
            <line
              x1="3.5"
              y1="8"
              x2="12.5"
              y2="8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg
            viewBox="0 0 16 16"
            className="vq-checkbox__check"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 8.5l3 3 7-7" />
          </svg>
        )}
      </span>
      {label !== undefined ? <span className="vq-checkbox__label">{label}</span> : null}
    </label>
  );
}
